import { useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/utils/logger';
import { supabase } from '@/integrations/supabase/client';

/**
 * Security event types tracked by the monitor
 */
interface SecurityEvent {
  type: 'suspicious_login' | 'multiple_sessions' | 'unusual_activity';
  details: Record<string, any>;
  timestamp: Date;
}

/**
 * Hook for monitoring security events and user session activity
 * 
 * @description
 * Provides comprehensive security monitoring including:
 * - Automatic session timeout after 30 minutes of inactivity
 * - Device fingerprinting for suspicious login detection
 * - Anomaly detection for rapid request patterns
 * - Activity tracking across user interactions
 * 
 * @example
 * ```tsx
 * function App() {
 *   const security = useSecurityMonitor();
 *   
 *   // Log a custom security event
 *   security.logSecurityEvent({
 *     type: 'unusual_activity',
 *     details: { action: 'bulk_delete', count: 100 },
 *     timestamp: new Date()
 *   });
 *   
 *   return <YourApp />;
 * }
 * ```
 * 
 * @returns Security monitoring utilities
 */
export function useSecurityMonitor() {
  const { user, session } = useAuth();
  const { toast } = useToast();
  const lastActivityRef = useRef<Date>(new Date());
  const sessionTimeoutRef = useRef<NodeJS.Timeout>();
  
  /** Session timeout duration: 120 minutes of inactivity */
  const SESSION_TIMEOUT = 120 * 60 * 1000;
  
  useEffect(() => {
    if (!user || !session) return;

    const resetTimeout = () => {
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }
      
      sessionTimeoutRef.current = setTimeout(() => {
        toast({
          title: "Sessão Expirada",
          description: "Por segurança, sua sessão foi encerrada por inatividade.",
          variant: "destructive",
        });
        supabase.auth.signOut();
      }, SESSION_TIMEOUT);
    };

    // Track user activity
    const trackActivity = () => {
      lastActivityRef.current = new Date();
      resetTimeout();
    };

    // Add activity listeners
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, trackActivity, true);
    });

    // Initial timeout setup
    resetTimeout();

    // Monitor for suspicious activities
    const monitorSuspiciousActivity = () => {
      // Check for multiple rapid requests (basic anomaly detection)
      const now = Date.now();
      const requests = JSON.parse(localStorage.getItem('recent_requests') || '[]');
      const recentRequests = requests.filter((time: number) => now - time < 60000);
      
      if (recentRequests.length > 500) {
        logSecurityEvent({
          type: 'unusual_activity',
          details: { 
            rapid_requests: recentRequests.length,
            user_id: user.id,
            timestamp: new Date().toISOString()
          },
          timestamp: new Date()
        });
      }

      // Update request history
      recentRequests.push(now);
      localStorage.setItem('recent_requests', JSON.stringify(recentRequests.slice(-100)));
    };

    // Monitor every 30 seconds
    const monitorInterval = setInterval(monitorSuspiciousActivity, 30000);

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, trackActivity, true);
      });
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }
      clearInterval(monitorInterval);
    };
  }, [user, session, toast]);

  /**
   * Log a security event for audit and monitoring
   * Events are stored locally and can trigger user notifications
   * 
   * @param event - Security event to log
   */
  const logSecurityEvent = async (event: SecurityEvent) => {
    try {
      logger.audit('Security Event Detected', event);
      
      // Store security event locally for analysis
      const securityLogs = JSON.parse(localStorage.getItem('security_logs') || '[]');
      securityLogs.push(event);
      
      // Keep only last 100 events
      localStorage.setItem('security_logs', JSON.stringify(securityLogs.slice(-100)));

      // For high-priority events, show user notification
      if (event.type === 'suspicious_login') {
        toast({
          title: "Atividade Suspeita Detectada",
          description: "Detectamos uma atividade incomum em sua conta. Verifique suas sessões ativas.",
          variant: "destructive",
        });
      }
    } catch (error) {
      logger.error('Failed to log security event', error, true);
    }
  };

  /**
   * Generate a device fingerprint for login anomaly detection
   * Creates a hash of browser and device characteristics
   * 
   * @returns 16-character fingerprint hash
   */
  const checkDeviceFingerprint = () => {
    // Basic device fingerprinting for security
    const fingerprint = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screen: `${screen.width}x${screen.height}`,
      platform: navigator.platform,
    };
    
    const fingerprintHash = btoa(JSON.stringify(fingerprint)).slice(0, 16);
    const storedFingerprint = localStorage.getItem('device_fingerprint');
    
    if (storedFingerprint && storedFingerprint !== fingerprintHash) {
      logSecurityEvent({
        type: 'suspicious_login',
        details: { 
          new_device: true,
          old_fingerprint: storedFingerprint,
          new_fingerprint: fingerprintHash
        },
        timestamp: new Date()
      });
    }
    
    localStorage.setItem('device_fingerprint', fingerprintHash);
    return fingerprintHash;
  };

  // Initialize device fingerprinting on first load
  useEffect(() => {
    if (user) {
      checkDeviceFingerprint();
    }
  }, [user]);

  return {
    logSecurityEvent,
    checkDeviceFingerprint,
  };
}
